import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LISTING_SEARCH_KEY, useFetchDynamicDeliveryPrice, useUserSelfDetails } from '@steelbuy/data-access';
import {
    FetchStatus,
    useAddressCollectionData,
    useListingBuyerEntityData,
    useOrderBuyerCreateEntityData,
} from '@steelbuy/data-provider';
import { useModal } from '@steelbuy/modal-dialog';
import {
    formatCurrency,
    AddressModel,
    ListingOrderModel,
    OfferListingStatus,
    OfferView,
    TradeUnitCalculation,
    getMinimumTargetPrice,
    getPricingUnit,
    getTradeUnitCalculation,
    ListingBuyerModel,
} from '@steelbuy/ts-shared';
import { CheckoutCartSummary, ListingBuyerTeaser } from '@steelbuy/ui-domain';
import {
    Accordion,
    AddressIdentifier,
    ButtonCallToAction,
    ButtonSecondary,
    Card,
    CardContentCheckoutPayment,
    CardContentDynamicDelivery,
    CardContentProductViewPrice,
    CardLayout,
    FormActionbar,
    FreightQuoteNotification,
    LoadingSpinner,
    LoadingStatus,
    Notification,
    NotificationLevel,
} from '@steelbuy/ui-primitive';
import { useWindowSize } from '@steelbuy/util';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { CheckoutCancelModal } from './CheckoutCancelModal';
import CheckoutCheckboxes from './CheckoutCheckboxes';
import { useCheckoutContext } from './CheckoutContext';
import { CheckoutErrorNotification } from './CheckoutErrorNotification';
import CheckoutPurchaseOrderNumber from './CheckoutPurchaseOrderNumber';
import { CHOOSE_ADDRESS_RELATIVE_PATH } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { AddressDetails } from '../address-details/AddressDetails';
import { Negotiations } from '../offers/Negotiations';
import { PageHeader } from '../page-header/PageHeader';

import './CheckoutForm.scss';

interface CheckoutFormProps {
    listingDetailPath: string;
}

export const CheckoutForm = ({ listingDetailPath }: CheckoutFormProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);

    const navigate = useNavigate();
    const { width } = useWindowSize();
    const SHOW_LARGE_SCREEN_SIZE = 1327;
    const checkoutContext = useCheckoutContext();
    const queryClient = useQueryClient();
    const {
        formState: { isValid },
        control,
        trigger,
    } = useForm({ mode: 'all' });

    const { invoiceAddressCollectionData, warehouseAddressCollectionData } = useAddressCollectionData();
    const listingEntityData = useListingBuyerEntityData();
    const orderCreateData = useOrderBuyerCreateEntityData();
    const { data: userData } = useUserSelfDetails();

    const listingModel = listingEntityData.query().get();
    const { data: listingDetails, isLoading: isListingDetailsLoading } = useFetchDynamicDeliveryPrice({
        listingId: listingModel?.id ?? '',
        queryOptions: { enabled: false },
    });
    const listing = listingDetails ?? listingModel;

    const isFreightQuoteUnavailable =
        !isListingDetailsLoading && !listingDetails?.pricing?.priceIncludingEstimatedDelivery?.value;

    const cancelModal = useModal();

    const [showPriceUpdateError, setShowPriceUpdateError] = useState(false);

    const invoiceAddresses = invoiceAddressCollectionData.query();
    const warehouseAddresses = warehouseAddressCollectionData.query();

    const defaultInvoiceAddressId = invoiceAddresses[0]?.id ?? '';
    const defaultWarehouseAddressId =
        (warehouseAddresses.find((address: AddressModel) => address.primary) ?? warehouseAddresses[0])?.id ?? '';

    listingEntityData.useFetchStatusEffect([FetchStatus.IDLE], () => {
        listingEntityData.fetch();
    });

    const checkoutPrice = useRef<number | null>(null); // only gets set when user clicks checkout
    const isLoading = listingEntityData.queryFetchStatus() === FetchStatus.PENDING;

    const isDynamicDeliveryPriceEnabled = useIsVendorFeatureSupported('DYNAMIC_DELIVERY_PRICE');

    const dynamicDeliveryCheckoutPrice =
        (isDynamicDeliveryPriceEnabled && listing?.pricing?.priceIncludingEstimatedDelivery?.value) ?? 0;

    const pricingField = listing?.offerPricing ? 'offerPricing' : 'pricing';

    const acceptedOfferPrice = isDynamicDeliveryPriceEnabled
        ? listing?.offerPricing?.priceIncludingEstimatedDelivery?.value
        : listing?.offerPricing?.priceIncludingDelivery.value;

    const createPurchaseOrder = () => {
        if (checkoutContext.deliveryAddressId && listingModel) {
            const acceptedNegotiation = listingModel.negotiations?.find(
                (negotiation) => negotiation.status === 'ACCEPTED'
            );
            orderCreateData.create({
                listingId: listingModel.id,
                negotiationId: acceptedNegotiation?.id,
                invoiceAddressId: defaultInvoiceAddressId,
                deliveryAddressId: checkoutContext.deliveryAddressId,
                additionalDeliveryInformation: checkoutContext.additionalDeliveryInformation,
                buyerOrderNumber: checkoutContext.buyerOrderNumber?.trim(),
                haulierRequired: checkoutContext.haulierRequired,
            } as ListingOrderModel);
        }
    };

    listingEntityData.useFetchStatusEffect([FetchStatus.SUCCESS], () => {
        if (checkoutPrice.current && checkoutContext.deliveryAddressId && listingModel) {
            if (
                checkoutPrice.current !==
                (isDynamicDeliveryPriceEnabled
                    ? listingModel.pricing.priceIncludingEstimatedDelivery?.value
                    : listingModel.pricing.priceIncludingDelivery.value)
            ) {
                if (isDynamicDeliveryPriceEnabled) {
                    queryClient.setQueryData<ListingBuyerModel>([LISTING_SEARCH_KEY, listingModel.id], (cache) => {
                        if (!cache) {
                            return cache;
                        }

                        return listingModel;
                    });
                }
                checkoutContext.setAgreedAuthorised(false);
                checkoutContext.setAgreedTermsAndConditions(false);
                setShowPriceUpdateError(true);

                return;
            }
            createPurchaseOrder();
        }
        checkoutPrice.current = null;
    });

    useEffect(() => {
        if (!checkoutContext.deliveryAddressId) {
            checkoutContext.setDeliveryAddressId(defaultWarehouseAddressId);
        }
    }, [defaultWarehouseAddressId]);

    useEffect(() => {
        if (isDynamicDeliveryPriceEnabled) {
            checkoutContext.setHaulierRequired(false);
        }
    }, []);

    useEffect(() => {
        if (!isValid && (checkoutContext.agreedTermsAndConditions || checkoutContext.agreedAuthorised)) {
            trigger();
        }
    }, [checkoutContext.agreedTermsAndConditions, checkoutContext.agreedAuthorised]);

    if (!listingModel) {
        return <LoadingSpinner />;
    }

    const buyIsDisabled = () => {
        if (!checkoutContext.agreedTermsAndConditions || !isValid || checkoutContext.haulierRequired === undefined) {
            return true;
        }
        return !checkoutContext.agreedAuthorised;
    };

    const handleCheckout = () => {
        if (!checkoutContext.deliveryAddressId) {
            return;
        }

        if (listingModel.offerPricing?.priceIncludingDelivery) {
            createPurchaseOrder();
            return;
        }

        checkoutPrice.current = dynamicDeliveryCheckoutPrice || listingModel.pricing.priceIncludingDelivery.value;
        listingEntityData.resolveFetchStatus(); // fetch listing to check if price has changed
    };

    const acceptedNegotiation = listingModel.negotiations?.find(
        (negotiation) => negotiation.status === OfferListingStatus.ACCEPTED
    );

    const weightOrItems =
        getTradeUnitCalculation(listingModel.material) === TradeUnitCalculation.BY_WEIGHT
            ? listingModel.weight
            : listingModel.numberOfItems || 0;

    let errorMessage = '';
    if (
        !checkoutContext.agreedTermsAndConditions ||
        !checkoutContext.agreedAuthorised ||
        !isValid ||
        checkoutContext.haulierRequired === undefined
    ) {
        errorMessage = t('translation:application.checkout.incompleteInformationNotification');
    }

    return (
        <div className="checkout">
            <PageHeader
                pageTitle={t('translation:application.checkout.header.title')}
                previousPageTitle={
                    acceptedNegotiation ? t('translation:application.checkout.offerDetails') : t('uiDomain:common.back')
                }
                onBackClick={() => {
                    checkoutContext.setBuyerOrderNumber('');
                    navigate(listingDetailPath);
                }}
                showCartIcon={false}
            />

            <TableLayout>
                {showPriceUpdateError && (
                    <Notification
                        level={NotificationLevel.WARNING}
                        message={t('translation:application.checkout.priceUpdatedError')}
                        className="price-updated-error"
                        disableScroll={false}
                    />
                )}
            </TableLayout>

            <div className="checkout-view">
                <div className="checkout-view-details">
                    {acceptedNegotiation && (
                        <Negotiations
                            negotiations={[acceptedNegotiation]}
                            view={OfferView.BUYER}
                            onStatusChange={() => {}}
                            pricingUnit={getPricingUnit(listingModel.material)}
                            weightOrItems={weightOrItems}
                            minimumPrice={getMinimumTargetPrice(
                                listingModel.material,
                                listingModel.weight,
                                listingModel.numberOfItems
                            )}
                            listing={listingModel}
                        />
                    )}
                    <Card isClickable={false}>
                        <CardLayout>
                            <AddressDetails
                                addressId={defaultInvoiceAddressId}
                                type={AddressIdentifier.BILLING_ADDRESS}
                                additionalInfoLabel={t(
                                    'translation:application.addressDetails.additionalInformationLabel'
                                )}
                            />
                        </CardLayout>
                    </Card>
                    {/* buyer purchase order number input field */}

                    <CheckoutPurchaseOrderNumber
                        setBuyerOrderNumber={checkoutContext.setBuyerOrderNumber}
                        control={control}
                        defaultValue={checkoutContext.buyerOrderNumber}
                    />

                    {/* payment  */}
                    <Card isClickable={false}>
                        <CardLayout>
                            <CardContentCheckoutPayment
                                header={t('translation:application.checkout.payment.sectionHeading')}
                                notificationMessage={t(
                                    'translation:application.checkout.payment.creditInfoNotification'
                                )}
                            />
                        </CardLayout>
                    </Card>
                    <Card isClickable={false}>
                        <CardLayout>
                            <AddressDetails
                                addressId={checkoutContext.deliveryAddressId}
                                type={AddressIdentifier.DELIVERY_ADDRESS}
                                onButtonClick={
                                    isDynamicDeliveryPriceEnabled
                                        ? undefined
                                        : () => navigate(CHOOSE_ADDRESS_RELATIVE_PATH)
                                }
                                additionalText={checkoutContext.additionalDeliveryInformation ?? ''}
                                onChangeAdditionalText={(value) =>
                                    checkoutContext.setAdditionalDeliveryInformation(value)
                                }
                                additionalInfoLabel={t(
                                    'translation:application.addressDetails.deliveryInformationLabel'
                                )}
                                additionalInfoPlaceholder={t(
                                    'translation:application.pickupAddressStep.additionalInfoPlaceholder'
                                )}
                                isCartCheckout
                                isMandatory
                            />
                        </CardLayout>
                    </Card>
                    <Card isClickable={false}>
                        <ListingBuyerTeaser
                            listing={listingModel}
                            offerValue={acceptedOfferPrice}
                            {...listingModel}
                            checkoutContext={checkoutContext}
                            isCart
                            isCheckout
                            isCartCheckout
                        />
                    </Card>
                    {/* Check boxes */}

                    <div className="input-checkbox-container">{userData ? <CheckoutCheckboxes /> : null}</div>

                    {errorMessage && <CheckoutErrorNotification errorMessage={errorMessage} />}

                    <FormActionbar>
                        <ButtonSecondary label={t('uiDomain:common.cancel')} onClick={cancelModal.show} />
                        <ButtonCallToAction
                            label={t('translation:application.checkout.submitCheckoutButtonLabel')}
                            disabled={buyIsDisabled() || isLoading}
                            onClick={() => handleCheckout()}
                            loadingStatus={isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                        />
                    </FormActionbar>

                    <CheckoutCancelModal
                        modalAccessor={cancelModal}
                        action={() => {
                            checkoutContext.reset();
                            navigate(listingDetailPath);
                        }}
                    />
                    {/* </DetailsLayout> */}
                </div>
                <div className="checkout-view-summary">
                    <div className="checkout-view-summary__list">
                        <Card isClickable={false}>
                            <CardLayout>
                                {width > SHOW_LARGE_SCREEN_SIZE ? (
                                    <div className="checkout-view-summary__list__withoutAccordion">
                                        <div className="checkout-view-summary__list__items">
                                            {t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                        </div>
                                        <div>
                                            <CheckoutCartSummary
                                                cartData={{
                                                    id: listingModel.id,
                                                    status: '',
                                                    createdAt: {
                                                        discriminator: 'Timestamp',
                                                        value: '',
                                                    },
                                                    product: {
                                                        ...listingModel,
                                                        pricing: listingDetails?.pricing ?? listingModel.pricing,
                                                    },
                                                    productType: 'LISTING',
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="checkout-view-summary__list__withAccordion">
                                        <Accordion
                                            collapsedLabel={t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                            extendedLabel={t('uiDomain:checkoutCart.item', {
                                                count: 1,
                                            })}
                                        >
                                            <div>
                                                <CheckoutCartSummary
                                                    cartData={{
                                                        id: listingModel.id,
                                                        status: '',
                                                        createdAt: {
                                                            discriminator: 'Timestamp',
                                                            value: '',
                                                        },
                                                        product: {
                                                            ...listingModel,
                                                            pricing: listingDetails?.pricing ?? listingModel.pricing,
                                                        },
                                                        productType: 'LISTING',
                                                    }}
                                                />
                                            </div>
                                        </Accordion>
                                    </div>
                                )}
                            </CardLayout>
                        </Card>
                    </div>
                    <div className="checkout-view-summary__price">
                        <Card isClickable={false}>
                            <CardLayout>
                                {isDynamicDeliveryPriceEnabled ? (
                                    <CardContentDynamicDelivery
                                        totalPrice={
                                            listing?.[pricingField]?.totalIncludingVat.value
                                                ? formatCurrency(
                                                      listing?.[pricingField]?.totalIncludingVat.value,
                                                      listingModel.pricing.currencyCode
                                                  ) ?? ''
                                                : ''
                                        }
                                        typeWithWeight={[
                                            {
                                                type: listingModel.material.materialType,
                                                weight: listingModel.weight,
                                                tradeUnit: listingModel.tradeUnit,
                                            },
                                        ]}
                                        totalPriceLablel={
                                            isFreightQuoteUnavailable
                                                ? t('uiDomain:listingBuyerDetailsPrice.totalExclDelivery')
                                                : t('uiDomain:listingBuyerDetailsPrice.deliveredTotal')
                                        }
                                    />
                                ) : (
                                    <CardContentProductViewPrice
                                        header={t('translation:application.checkoutDeliveryOption.orderSummary')}
                                        pricePerUnit=""
                                        pricePerUnitLabel={t(
                                            'translation:application.checkoutDeliveryOption.totalWeight'
                                        )}
                                        isCartCheckout
                                        weight=""
                                        typeWithWeight={[
                                            {
                                                type: listingModel.material.materialType,
                                                weight: listingModel.weight,
                                                tradeUnit: listingModel.tradeUnit,
                                            },
                                        ]}
                                        totalExVat={
                                            formatCurrency(
                                                listingModel.offerPricing?.totalExcludingVat.value ??
                                                    listingModel.pricing.totalExcludingVat.value,
                                                listingModel.pricing.currencyCode
                                            ) ?? ''
                                        }
                                        vatPercent={
                                            (
                                                (listingModel.offerPricing?.taxPercent ??
                                                    listingModel.pricing.taxPercent) / 100
                                            ).toString() ?? '0'
                                        }
                                        totalPrice={
                                            formatCurrency(
                                                listingModel.offerPricing?.totalIncludingVat.value ??
                                                    listingModel.pricing.totalIncludingVat.value,
                                                listingModel.pricing.currencyCode
                                            ) ?? ''
                                        }
                                        vat={
                                            formatCurrency(
                                                listingModel.offerPricing?.vat.value ?? listingModel.pricing.vat.value,
                                                listingModel.pricing.currencyCode
                                            ) ?? ''
                                        }
                                    />
                                )}
                            </CardLayout>
                        </Card>
                    </div>
                    <FreightQuoteNotification
                        isDynamicDeliveryPriceEnabled={isDynamicDeliveryPriceEnabled}
                        error={isFreightQuoteUnavailable}
                    />
                </div>
            </div>
        </div>
    );
};
